import React from "react";
import { Link } from "react-router-dom";
import Window from "../components/Window";
import Icon, { IconName, IconTile } from "../components/Icon";

// All the ways to take part, from writing a letter to helping hold the space.
type Way = { icon: IconName; title: string; body: React.ReactNode };

const ANYONE: Way[] = [
  {
    icon: "mail",
    title: "Write letters",
    body: (
      <>
        The heart of it all. Pin a letter to the <Link to="/board">PenPal Board</Link>,
        answer someone else's, and write at whatever pace you like.
      </>
    ),
  },
  {
    icon: "community",
    title: "Start a conversation",
    body: (
      <>
        Post in the <Link to="/community">Community Rooms</Link>, or reply to
        someone who's waiting for a kind word. Anonymous is okay too.
      </>
    ),
  },
  {
    icon: "gift",
    title: "Send kudos",
    body: <>A little note for someone who made your day. It costs nothing and means a lot.</>,
  },
  {
    icon: "edit",
    title: "Write for the PenPal Post",
    body: (
      <>
        Poems, essays, stories, little histories. Drop them in the{" "}
        <Link to="/post">PenPal Post</Link> mailbox. A real person reads
        everything first, so it can take a day or two to show up.
      </>
    ),
  },
];

const ROLES: Way[] = [
  {
    icon: "lock",
    title: "Quartermaster",
    body: (
      <>
        Start a <Link to="/circles">Private Circle</Link> for folks navigating
        something similar, and look after it: let people in, keep it kind, and
        hide posts that break the circle's guidelines. You ask to start one,
        and we approve it.
      </>
    ),
  },
  {
    icon: "heart",
    title: "First mate",
    body: (
      <>
        Quartermasters can name up to two first mates to help out. If the
        quartermaster steps away, a first mate takes the helm so the circle
        keeps going.
      </>
    ),
  },
  {
    icon: "book",
    title: "PenPal Post editor",
    body: (
      <>
        Read submissions in the sorting room, deliver them to the Post or send
        them back with a kind note, and write the occasional dispatch.{" "}
        <strong>By invitation.</strong> Interested? Email us.
      </>
    ),
  },
  {
    icon: "shield",
    title: "Moderator",
    body: (
      <>
        Help read reports and keep the community safe, with care and
        accountability rather than punishment. <strong>By invitation</strong>,
        for folks who've been around a while. We especially want queer and
        trans Black, Indigenous, and people of color helping hold this space.
      </>
    ),
  },
];

const MORE: Way[] = [
  { icon: "send", title: "Tell a friend", body: <>Know someone who'd love a pen pal? Send them our way.</> },
  {
    icon: "message",
    title: "Tell us what you think",
    body: <>Ideas, bugs, things that felt off, things you loved. We read every email.</>,
  },
  {
    icon: "heart",
    title: "Grow our Care page",
    body: (
      <>
        Know a support line run by and for our communities that should be on{" "}
        <Link to="/care">Care &amp; resources</Link>, especially outside the US
        and Canada? Let us know.
      </>
    ),
  },
];

function Ways({ title, ways }: { title: string; ways: Way[] }) {
  return (
    <section className="profile-box bg-white">
      <div className="profile-box-body">
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        <ul className="mt-2 flex flex-col gap-3 text-sm">
          {ways.map((w) => (
            <li key={w.title} className="flex gap-3">
              <span className="mt-0.5">
                <Icon name={w.icon} size={20} tone="pink" pop />
              </span>
              <span>
                <strong>{w.title}.</strong> {w.body}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function GetInvolved() {
  return (
    <Window title="get_involved.txt" tone="blue" minimizable={false}>
      <h1 className="window-heading flex items-center gap-3">
        <IconTile name="community" tone="blue" size={44} /> Get involved
      </h1>
      <p className="window-sub">
        PenPal Board grows with the people in it. Here's how to take part, a
        little or a lot.
      </p>
      <div className="flex flex-col gap-4">
        <Ways title="Anyone can" ways={ANYONE} />
        <Ways title="Roles with a bit more responsibility" ways={ROLES} />
        <Ways title="Other ways to help" ways={MORE} />
        <p className="text-sm">
          Want to help as an editor or moderator, or just say hi? Email us at{" "}
          <a href="mailto:2slgbtqiapenpal@gmail.com">2slgbtqiapenpal@gmail.com</a>.{" "}
          <span className="font-hand text-lg">-Slug &amp; Worm</span>
        </p>
      </div>
    </Window>
  );
}
